/**
 * Typography checks merged into "Check protocol style".
 * Compares inline font-family / font-size in section HTML to FONT_CONFIG
 * (Draft 1.0 Style Guide for Regulatory Documents — §4.1 FORMATTING STANDARDS).
 * YAML text rules cannot see fonts; this runs client-side on stored HTML.
 */

function normalizeFamily(name) {
  if (!name) return '';
  const first = String(name).split(',')[0].replace(/['"]/g, '').trim().toLowerCase();
  const map = {
    times: 'times new roman',
    'times new roman': 'times new roman',
    tms: 'times new roman',
    arial: 'arial',
    helvetica: 'arial',
    calibri: 'calibri',
    cambria: 'cambria',
    georgia: 'georgia',
    verdana: 'verdana',
  };
  return map[first] || first;
}

function parseInlineStyle(styleAttr) {
  const out = { fontFamily: null, fontSizeRaw: null };
  if (!styleAttr || typeof styleAttr !== 'string') return out;
  styleAttr.split(';').forEach((part) => {
    const idx = part.indexOf(':');
    if (idx < 0) return;
    const prop = part.slice(0, idx).trim().toLowerCase();
    const val = part.slice(idx + 1).trim();
    if (prop === 'font-family') out.fontFamily = val;
    if (prop === 'font-size') out.fontSizeRaw = val;
  });
  return out;
}

function sizeToPoints(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  let m = /^([\d.]+)\s*pt$/.exec(s);
  if (m) return parseFloat(m[1]);
  m = /^([\d.]+)\s*px$/.exec(s);
  if (m) return parseFloat(m[1]) * 0.75;
  m = /^([\d.]+)$/.exec(s);
  if (m) return parseFloat(m[1]);
  return null;
}

function inferRole(tag) {
  const t = (tag || '').toLowerCase();
  // Draft 1.0 PDF §4.1: C-Heading 1 = 14 pt (body section headings). C-Title 18 pt is title-page export, not typical <h1> in sections.
  if (t === 'h1') return 'heading';
  if (t === 'h2') return 'subheading';
  if (t === 'h3') return 'heading3';
  if (t === 'h4') return 'heading4';
  if (t === 'h5' || t === 'h6') return 'heading5';
  if (t === 'th') return 'tableHeader';
  if (t === 'td') return 'tableCell';
  if (t === 'li') return 'list';
  if (t === 'figcaption') return 'caption';
  return 'body';
}

function configKeyForRole(role) {
  if (role === 'title') return 'title';
  if (role === 'heading') return 'heading';
  if (role === 'subheading') return 'subheading';
  if (role === 'heading3') return 'heading3';
  if (role === 'heading4') return 'heading4';
  if (role === 'heading5') return 'heading5';
  if (role === 'caption') return 'caption';
  if (role === 'tableHeader') return 'tableHeader';
  if (role === 'tableCell') return 'tableCell';
  if (role === 'list') return 'list';
  if (role === 'footnote') return 'footnote';
  if (role === 'headerFooter') return 'headerFooter';
  return 'body';
}

function buildFinding({
  id,
  ruleId,
  title,
  message,
  severity,
  sectionId,
  sectionTitle,
  matchedText,
}) {
  return {
    id,
    ruleId,
    title,
    message,
    severity,
    category: 'typography',
    sectionId,
    sectionTitle,
    sectionType: 'html_typography',
    startOffset: 0,
    endOffset: 0,
    matchedText: matchedText || '',
    suggestedReplacement: undefined,
    autofixSafe: false,
    classification: 'client-html-check',
    rationale:
      'Inline styles are compared to FONT_CONFIG (Draft 1.0 Style Guide §4.1 / Word export). Unstyled content inherits export defaults and is not flagged.',
  };
}

/**
 * @param {string} html
 * @param {string} sectionId
 * @param {string} sectionTitle
 * @param {object} fontConfig - DocumentEditor FONT_CONFIG
 * @param {() => string} nextId
 */
export function lintHtmlTypography(html, sectionId, sectionTitle, fontConfig, nextId) {
  if (typeof document === 'undefined' || !html || typeof html !== 'string') return [];
  const findings = [];
  let wrap;
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="typo-lint-root">${html}</div>`,
      'text/html',
    );
    wrap = doc.getElementById('typo-lint-root');
  } catch {
    return [];
  }
  if (!wrap) return [];

  const all = wrap.querySelectorAll('*[style], font[face], font[size]');
  all.forEach((el) => {
    const styleAttr = el.getAttribute('style') || '';
    const parsed = parseInlineStyle(styleAttr);
    let fontFamily = parsed.fontFamily;
    if (!fontFamily && el.tagName === 'FONT') {
      fontFamily = el.getAttribute('face') || '';
    }

    const role = inferRole(el.tagName);
    const key = configKeyForRole(role);
    const expected = fontConfig[key] || fontConfig.body;
    const expectedName = normalizeFamily(expected.name);
    const expectedSize = Number(expected.size);

    const declaredFam = fontFamily ? normalizeFamily(fontFamily) : null;
    const declaredPt = parsed.fontSizeRaw ? sizeToPoints(parsed.fontSizeRaw) : null;

    if (el.tagName === 'FONT' && el.getAttribute('size') && !parsed.fontSizeRaw) {
      findings.push(
        buildFinding({
          id: nextId(),
          ruleId: 'FONT_LEGACY',
          title: 'Legacy <font size> markup',
          message: `Found <font size="..."> in "${sectionTitle || sectionId}". Prefer semantic HTML; export profile uses ${expected.name} ${expected.size}pt for ${role} (Draft 1.0 §4.1).`,
          severity: 'warning',
          sectionId,
          sectionTitle,
          matchedText: el.outerHTML?.slice(0, 120) || '<font>',
        }),
      );
    }

    if (!fontFamily && !parsed.fontSizeRaw && el.tagName !== 'FONT') return;

    const familyMismatch =
      Boolean(declaredFam) && expectedName && declaredFam !== expectedName;
    const sizeMismatch =
      declaredPt != null &&
      !Number.isNaN(expectedSize) &&
      Math.abs(declaredPt - expectedSize) > 0.6;

    if (!familyMismatch && !sizeMismatch) return;

    const parts = [];
    if (familyMismatch) {
      parts.push(
        `font-family is "${(fontFamily || '').trim()}" but export profile expects "${expected.name}" for <${el.tagName.toLowerCase()}> (${role}).`,
      );
    }
    if (sizeMismatch) {
      parts.push(
        `font-size is ~${declaredPt.toFixed(1)}pt but export profile expects ${expectedSize}pt for ${role} (Draft 1.0 §4.1).`,
      );
    }

    findings.push(
      buildFinding({
        id: nextId(),
        ruleId: 'FONT_INLINE',
        title: 'Inline font does not match export typography profile',
        message: parts.join(' '),
        severity: 'warning',
        sectionId,
        sectionTitle,
        matchedText: (styleAttr || el.outerHTML || '').slice(0, 200),
      }),
    );
  });

  return findings;
}

let idSeq = 0;

/**
 * @param {Array} sections - editor sections with output / content
 * @param {object} fontConfig - FONT_CONFIG from DocumentEditor
 */
export function lintProtocolTypographyForSections(sections, fontConfig) {
  if (!Array.isArray(sections) || !fontConfig) return [];
  const out = [];
  const nextId = () => `typo-${Date.now()}-${++idSeq}`;

  const walk = (sec) => {
    if (!sec) return;
    const sid = String(sec._id || sec.sectionId || sec.id || '');
    const title = sec.title || '';
    const html = sec.output || sec.content || '';
    if (html) {
      out.push(...lintHtmlTypography(html, sid, title, fontConfig, nextId));
    }
    (sec.subsections || []).forEach(walk);
  };

  sections.forEach(walk);
  return out.slice(0, 400);
}

export function mergeTypographyIntoLintResult(serverResult, typographyFindings) {
  if (!serverResult) return serverResult;
  const base = serverResult.findings || [];
  const combined = [...base, ...(typographyFindings || [])];
  const sev = { error: 0, warning: 0, info: 0 };
  combined.forEach((f) => {
    const s = (f.severity || 'warning').toLowerCase();
    if (sev[s] !== undefined) sev[s]++;
  });
  const auto = combined.filter((f) => f.autofixSafe).length;
  const inlineFontFindingsCount = (typographyFindings || []).filter((f) =>
    String(f.ruleId || '').startsWith('FONT'),
  ).length;
  return {
    ...serverResult,
    findings: combined,
    summary: {
      ...(serverResult.summary || {}),
      totalFindings: combined.length,
      autofixableCount: auto,
      inlineFontFindingsCount,
      bySeverity: {
        error: sev.error,
        warning: sev.warning,
        info: sev.info,
      },
    },
    diagnostics: [
      ...(serverResult.diagnostics || []),
      ...(typographyFindings?.length
        ? [
            {
              ruleId: 'TYPO_CLIENT',
              title: 'Typography (HTML)',
              status: 'ok',
              scope: 'lint',
              level: 'info',
              reason:
                'FONT_* rules compare inline HTML to FONT_CONFIG (Draft 1.0 Style Guide §4.1). Plain unstyled text uses export defaults.',
            },
          ]
        : []),
    ],
  };
}
